// https://stripe.com/docs/payments/accept-a-payment-synchronously
import { Injectable, Inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import * as Stripe from '@stripe/stripe-js';

import * as Tokens from '@shared/core/tokens';

import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { StripePaymentProviderMapper } from '@shared/core/mappers/paymentProviders/stripe.payment-provider.shared.mapper';

@Injectable({
    providedIn: 'root',
})
export class StripePaymentProviderService {
    private _instance: Stripe.Stripe;
    private _elements: Stripe.StripeElements;
    private _htmlElementsRegistry: Stripe.StripeHtmlElementsRegistry = {};

    private _scriptElem: HTMLScriptElement;

    private _commonStyles: Stripe.StripeElementStyle = {
        base: {
            fontSize: '16px',
        },
        invalid: {
            color: 'red',
            iconColor: 'red',
        },
    };

    private _stripeApiUrl = 'https://api.stripe.com/v1';

    constructor(@Inject(Tokens.CONFIG_TOKEN) public config: OLO.Config, public httpClient: HttpClient) {}

    public requestConfig(locationNo: number): Observable<OLO.DTO.StripeSettingsResponse> {
        if (!locationNo) {
            return throwError('No locationNo provided for Stripe payment provider');
        }

        return this._getSettingsForLocation(locationNo);
    }

    protected _getSettingsForLocation(locationNo: number): Observable<OLO.DTO.StripeSettingsResponse> {
        return this.httpClient.get<APIv3.PaymentsGetStripeSettings.Responses.$200>(`${this.config.api.base}/Payments/stripe/settings/${locationNo}`).pipe(
            map((response) => StripePaymentProviderMapper.mapStripePaymentGetSettingsForLocation(response)),
            catchError((ex) => {
                console.error('LocationNo not provided', ex);

                return throwError(ex);
            }),
        );
    }

    public async addHtmlElementsToDOM(): Promise<boolean> {
        return this._addScriptElement();
    }

    public setupForm(
        config: OLO.DTO.StripeSettingsResponse,
        targetHtmlElements: Stripe.StripeTargetElementsList,
        options: Stripe.ElementsConfiguration = {},
    ): Stripe.StripeInitResult {
        if (this._elements || this._instance) {
            this.destroyForm();
        }

        this._instance = window.Stripe(config.ApiKey, {
            locale: this.config.localization.country.toLowerCase() as any,
        });
        this._elements = this._instance.elements();

        for (const fieldType in targetHtmlElements) {
            this._htmlElementsRegistry[fieldType] = this._elements.create(fieldType as any, {
                ...options[fieldType],
                style: {
                    ...this._commonStyles,
                    ...options[fieldType]?.style,
                },
            });

            this._htmlElementsRegistry[fieldType].mount(targetHtmlElements[fieldType]);
        }

        return {
            registry: this._htmlElementsRegistry,
            stripe: this._instance,
        };
    }

    /* eslint-disable @typescript-eslint/naming-convention */
    public tokenizePayment(tokenizedBody: object, apiKey: string): Observable<string> {
        const body = { pk_token: { ...tokenizedBody } };

        const urlSearchParams = new URLSearchParams();
        Object.keys(body).forEach((key) => urlSearchParams.append(key, JSON.stringify(body[key])));

        return this.httpClient
            .post<{ id: string }>(`${this._stripeApiUrl}/tokens`, urlSearchParams.toString(), {
                headers: new HttpHeaders({
                    // eslint-disable-next-line prettier/prettier
                    'Authorization': `Bearer ${apiKey}`,
                    'Content-Type': 'application/x-www-form-urlencoded',
                }),
            })
            .pipe(map((response) => response.id));
    }
    /* eslint-enable @typescript-eslint/naming-convention */

    public destroyForm(): void {
        this._instance = null;
        this._elements = null;
        this._htmlElementsRegistry = {};
    }

    protected async _addScriptElement(): Promise<boolean> {
        if (this._scriptElem) return null;

        return new Promise((resolve, reject) => {
            this._scriptElem = document.createElement('script');
            this._scriptElem.src = 'https://js.stripe.com/v3/';
            this._scriptElem.crossOrigin = 'anonymous';

            this._scriptElem.onload = () => resolve(true);
            this._scriptElem.onerror = () => reject('Unable to load stripe script element');

            document.body.appendChild(this._scriptElem);
        });
    }

    protected _removeScriptElement(): void {
        if (!this._scriptElem) return;

        document.body.removeChild(this._scriptElem);
        this._scriptElem = null;
    }
}
